import '../../../shared/styles/list.css';

import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  TablePaginationConfig,
  Tag,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { PlusCircleTwoTone, OrderedListOutlined } from '@ant-design/icons';

import { getAdminInformation } from '../../../services/adminApi';
import {
  createServiceTier,
  deleteServiceTier,
  getAvailableServiceTypesForTier,
  getServiceTier,
  getServiceTierPaginatedList,
  patchServiceTier,
  reorderServiceTiers,
} from '../../../services/serviceTierApi';
import AdminDTO from '../../../shared/dtos/adminDTO';
import ServiceTierDTO from '../../../shared/dtos/serviceTierDTO';
import ServiceTypeDTO from '../../../shared/dtos/serviceTypeDTO';
import {
  filterServiceTierIcons,
  getServiceTierIconByKey,
} from '../../../shared/constants/serviceTierIconOptions';
import ContainerLayout from '../../common/containerLayout';
import ReorderModal from '../../common/ReorderModal';

type ScopeFilter = 'all' | 'global' | 'admin';
type ScopeFormValue = 'global' | 'admin';

interface ServiceTierFormValues {
  name: string;
  description?: string;
  iconKey?: string;
  percentage: number;
  scope: ScopeFormValue;
  adminId?: number;
  serviceTypeIds: number[];
}

const { Text } = Typography;
const { Search } = Input;

const ServiceTierList = () => {
  const [form] = Form.useForm<ServiceTierFormValues>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingAvailableServiceTypes, setLoadingAvailableServiceTypes] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isIconPickerOpen, setIsIconPickerOpen] = useState(false);
  const [iconSearchQuery, setIconSearchQuery] = useState('');
  const [serviceTiers, setServiceTiers] = useState<ServiceTierDTO[]>([]);
  const [admins, setAdmins] = useState<AdminDTO[]>([]);
  const [availableServiceTypes, setAvailableServiceTypes] = useState<ServiceTypeDTO[]>([]);
  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');
  const [adminFilter, setAdminFilter] = useState<number | undefined>(undefined);
  const [editingTierId, setEditingTierId] = useState<number | null>(null);
  const [isReorderModalOpen, setIsReorderModalOpen] = useState(false);
  const [tableParams, setTableParams] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const currentPage = tableParams.current || 1;
  const currentPageSize = tableParams.pageSize || 10;

  const scopeFormValue = Form.useWatch('scope', form);
  const adminIdFormValue = Form.useWatch('adminId', form);
  const iconKeyFormValue = Form.useWatch('iconKey', form);

  const adminOptions = useMemo(
    () =>
      admins.map((admin) => ({
        value: admin.id,
        label: admin.name,
      })),
    [admins],
  );

  const serviceTypeOptions = useMemo(
    () =>
      availableServiceTypes
        .filter((serviceType) => String(serviceType.type || 'NORMAL').toUpperCase() === 'TIER')
        .filter((serviceType) => Boolean(serviceType.id))
        .map((serviceType) => ({
          value: Number(serviceType.id),
          label: serviceType.name || `Tipo #${serviceType.id}`,
        })),
    [availableServiceTypes],
  );

  const filteredTierIcons = useMemo(
    () => filterServiceTierIcons(iconSearchQuery),
    [iconSearchQuery],
  );

  const selectedTierIcon = useMemo(
    () => getServiceTierIconByKey(iconKeyFormValue),
    [iconKeyFormValue],
  );

  const fetchOptions = useCallback(async () => {
    const adminResults = await getAdminInformation();

    setAdmins(adminResults || []);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const result = await getServiceTierPaginatedList({
        page: currentPage,
        take: currentPageSize,
        search,
        scope: scopeFilter,
        adminId: scopeFilter === 'admin' ? adminFilter : undefined,
      });

      setServiceTiers(result.data || []);
      setTableParams((previous) => ({
        ...previous,
        total: result.total || 0,
      }));
    } finally {
      setLoading(false);
    }
  }, [adminFilter, scopeFilter, search, currentPage, currentPageSize]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!isModalOpen || !scopeFormValue) {
      setAvailableServiceTypes([]);
      return;
    }

    if (scopeFormValue === 'admin' && !adminIdFormValue) {
      setAvailableServiceTypes([]);
      return;
    }

    let isCancelled = false;

    const syncAvailableServiceTypes = async () => {
      setLoadingAvailableServiceTypes(true);
      try {
        const serviceTypes = await getAvailableServiceTypesForTier({
          scope: scopeFormValue,
          adminId: adminIdFormValue,
          excludedTierId: editingTierId || undefined,
        });

        if (!isCancelled) {
          setAvailableServiceTypes(serviceTypes);
        }
      } finally {
        if (!isCancelled) {
          setLoadingAvailableServiceTypes(false);
        }
      }
    };

    syncAvailableServiceTypes();

    return () => {
      isCancelled = true;
    };
  }, [
    adminIdFormValue,
    editingTierId,
    isModalOpen,
    scopeFormValue,
  ]);

  const onSearch = (value: string) => {
    setSearch(value);
    setTableParams((previous) => ({
      ...previous,
      current: 1,
    }));
  };

  const handleScopeFilterChange = (value: ScopeFilter) => {
    setScopeFilter(value);
    if (value !== 'admin') {
      setAdminFilter(undefined);
    }
    setTableParams((previous) => ({
      ...previous,
      current: 1,
    }));
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setTableParams((previous) => ({
      ...previous,
      current: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };

  const openCreateModal = () => {
    setEditingTierId(null);
    setAvailableServiceTypes([]);
    form.setFieldsValue({
      name: '',
      description: '',
      iconKey: undefined,
      percentage: 0,
      scope: 'global',
      adminId: undefined,
      serviceTypeIds: [],
    });
    setIsModalOpen(true);
  };

  const openEditModal = async (id: number) => {
    setSaving(true);

    try {
      const tier = await getServiceTier(id);
      setEditingTierId(id);
      form.setFieldsValue({
        name: tier.name,
        description: tier.description || '',
        iconKey: tier.iconKey || undefined,
        percentage: tier.percentage,
        scope: tier.adminId ? 'admin' : 'global',
        adminId: tier.adminId || undefined,
        serviceTypeIds: tier.serviceTypeIds || [],
      });
      setIsModalOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    await deleteServiceTier(id);
    await fetchData();
  };

  const handleCancelModal = () => {
    form.resetFields();
    setAvailableServiceTypes([]);
    setEditingTierId(null);
    setIsModalOpen(false);
    setIsIconPickerOpen(false);
    setIconSearchQuery('');
  };

  const handleSubmit = async (values: ServiceTierFormValues) => {
    setSaving(true);

    try {
      const payload: ServiceTierDTO = {
        name: values.name,
        description: String(values.description || '').trim() || null,
        iconKey: values.iconKey || null,
        percentage: values.percentage,
        adminId: values.scope === 'admin' ? values.adminId : null,
        serviceTypeIds: values.serviceTypeIds || [],
      };

      if (editingTierId) {
        await patchServiceTier(editingTierId, payload);
      } else {
        await createServiceTier(payload);
      }

      setIsModalOpen(false);
      form.resetFields();
      setAvailableServiceTypes([]);
      setEditingTierId(null);
      await fetchData();
    } catch (error) {
      // Error notifications are handled by axios interceptor; avoid unhandled promise crash.
      return;
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<ServiceTierDTO> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80,
    },
    {
      title: 'Nombre',
      dataIndex: 'name',
    },
    {
      title: 'Descripcion',
      dataIndex: 'description',
      render: (_, record) => record.description || '-',
      width: 260,
    },
    {
      title: 'Icono',
      dataIndex: 'iconKey',
      width: 170,
      render: (_, record) => {
        const selectedIcon = getServiceTierIconByKey(record.iconKey);
        if (!selectedIcon) {
          return '-';
        }

        const Icon = selectedIcon.Icon;

        return (
          <Space>
            <Icon fontSize='small' />
            <span>{selectedIcon.label}</span>
          </Space>
        );
      },
    },
    {
      title: 'Porcentaje',
      dataIndex: 'percentage',
      render: (_, record) => `${record.percentage}%`,
      width: 130,
    },
    {
      title: 'Orden',
      dataIndex: 'sortOrder',
      width: 80,
      render: (_, record) => record.sortOrder ?? '-',
    },
    {
      title: 'Alcance',
      dataIndex: 'adminId',
      render: (_, record) => (record.adminId ? record.admin?.name || `Admin #${record.adminId}` : 'Global'),
      width: 220,
    },
    {
      title: 'Tipos de servicio',
      dataIndex: 'serviceTypes',
      render: (_, record) => (
        <Space wrap>
          {(record.serviceTypes || []).filter(Boolean).map((serviceType: any) => (
            <Tag key={`${serviceType.id || serviceType.name}`}>
              {serviceType.name || `Tipo #${serviceType.id}`}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button onClick={() => openEditModal(Number(record.id))}>Editar</Button>
          <Popconfirm
            title='Estas seguro de eliminar este tier?'
            onConfirm={() => handleDelete(Number(record.id))}
            okText='Si'
            cancelText='No'
          >
            <Button danger>Eliminar</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <ContainerLayout title='Tiers de servicio' showNotification={false}>
      <Col span={24}>
        <Space wrap>
          <Button type='primary' icon={<PlusCircleTwoTone />} onClick={openCreateModal}>
            Nuevo tier
          </Button>

          <Button icon={<OrderedListOutlined />} onClick={() => setIsReorderModalOpen(true)}>
            Reordenar
          </Button>

          <Text strong>Buscar</Text>
          <Search
            placeholder='Nombre del tier'
            allowClear
            onSearch={onSearch}
            loading={loading}
            disabled={loading}
          />

          <Text strong>Alcance</Text>
          <Select
            value={scopeFilter}
            onChange={handleScopeFilterChange}
            options={[
              { value: 'all', label: 'Todos' },
              { value: 'global', label: 'Globales' },
              { value: 'admin', label: 'Especificos' },
            ]}
            style={{ width: 160 }}
          />

          <Select
            placeholder='Admin'
            value={adminFilter}
            onChange={(value) => {
              setAdminFilter(value);
              setTableParams((previous) => ({
                ...previous,
                current: 1,
              }));
            }}
            allowClear
            disabled={scopeFilter !== 'admin'}
            options={adminOptions}
            style={{ width: 240 }}
          />
        </Space>
      </Col>

      <Col span={24} style={{ width: '100%', height: '83vh' }}>
        <Table<ServiceTierDTO>
          rowKey={(record) => String(record.id)}
          columns={columns}
          dataSource={serviceTiers}
          pagination={tableParams}
          loading={loading || saving}
          onChange={handleTableChange}
          scroll={{ y: 'calc(100vh - 290px)' }}
        />
      </Col>

      <Modal
        title={editingTierId ? 'Editar tier' : 'Crear tier'}
        open={isModalOpen}
        onCancel={handleCancelModal}
        onOk={() => form.submit()}
        confirmLoading={saving}
        okText={editingTierId ? 'Guardar' : 'Crear'}
      >
        <Form<ServiceTierFormValues>
          form={form}
          layout='vertical'
          onFinish={handleSubmit}
          initialValues={{
            scope: 'global',
            description: '',
            iconKey: undefined,
            percentage: 0,
            serviceTypeIds: [],
          }}
        >
          <Form.Item
            label='Nombre'
            name='name'
            rules={[{ required: true, message: 'Ingrese un nombre' }]}
          >
            <Input maxLength={120} />
          </Form.Item>

          <Form.Item
            label='Descripcion'
            name='description'
          >
            <Input.TextArea
              maxLength={255}
              showCount
              autoSize={{ minRows: 2, maxRows: 4 }}
              placeholder='Texto opcional mostrado en las cards de tier en manager-next'
            />
          </Form.Item>

          <Form.Item
            label='Porcentaje'
            name='percentage'
            rules={[{ required: true, message: 'Ingrese un porcentaje' }]}
          >
            <InputNumber min={0} max={500} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label='Icono (manager-next)'
            name='iconKey'
          >
            <Space direction='vertical' style={{ width: '100%' }}>
              <Button onClick={() => setIsIconPickerOpen(true)}>
                Seleccionar icono Material
              </Button>
              <Text type='secondary'>
                Se guarda la clave del icono MUI (ejemplo: StarBorderRounded).
              </Text>
              {selectedTierIcon && (
                <Space>
                  <selectedTierIcon.Icon fontSize='small' />
                  <Text>{selectedTierIcon.label}</Text>
                  <Button
                    type='link'
                    danger
                    onClick={() => form.setFieldValue('iconKey', undefined)}
                    style={{ padding: 0 }}
                  >
                    Quitar
                  </Button>
                </Space>
              )}
            </Space>
          </Form.Item>

          <Form.Item
            label='Alcance'
            name='scope'
            rules={[{ required: true, message: 'Seleccione un alcance' }]}
          >
            <Select
              options={[
                { value: 'global', label: 'Global' },
                { value: 'admin', label: 'Especifico por admin' },
              ]}
              onChange={(value: ScopeFormValue) => {
                form.setFieldValue('serviceTypeIds', []);
                if (value === 'global') {
                  form.setFieldValue('adminId', undefined);
                }
              }}
            />
          </Form.Item>

          {scopeFormValue === 'admin' && (
            <Form.Item
              label='Admin'
              name='adminId'
              rules={[{ required: true, message: 'Seleccione un admin' }]}
            >
              <Select
                options={adminOptions}
                showSearch
                optionFilterProp='label'
                onChange={() => form.setFieldValue('serviceTypeIds', [])}
              />
            </Form.Item>
          )}

          <Form.Item
            label='Tipos de servicio'
            name='serviceTypeIds'
            rules={[{ required: true, message: 'Seleccione al menos un tipo de servicio' }]}
          >
            <Select
              mode='multiple'
              options={serviceTypeOptions}
              optionFilterProp='label'
              showSearch
              disabled={
                loadingAvailableServiceTypes || (scopeFormValue === 'admin' && !adminIdFormValue)
              }
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title='Seleccionar icono Material'
        open={isIconPickerOpen}
        onCancel={() => setIsIconPickerOpen(false)}
        footer={null}
        width={980}
      >
        <Space direction='vertical' style={{ width: '100%' }} size={16}>
          <Input.Search
            placeholder='Buscar iconos (ej: premium, star, shield, car)'
            allowClear
            value={iconSearchQuery}
            onChange={(event) => setIconSearchQuery(event.target.value)}
          />

          <div style={{ maxHeight: 440, overflowY: 'auto', paddingRight: 6 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: 10,
              }}
            >
              <Button
                onClick={() => {
                  form.setFieldValue('iconKey', undefined);
                  setIsIconPickerOpen(false);
                }}
                style={{
                  width: '100%',
                  minHeight: 82,
                  height: 'auto',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  whiteSpace: 'normal',
                  padding: '12px 14px',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                  <Text strong style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                    Sin icono
                  </Text>
                  <Text type='secondary' style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                    Usar fallback visual
                  </Text>
                </div>
              </Button>

              {filteredTierIcons.map((iconOption) => {
                const Icon = iconOption.Icon;
                const isSelected = iconOption.key === selectedTierIcon?.key;

                return (
                  <Button
                    key={iconOption.key}
                    type={isSelected ? 'primary' : 'default'}
                    onClick={() => {
                      form.setFieldValue('iconKey', iconOption.key);
                      setIsIconPickerOpen(false);
                    }}
                    style={{
                      width: '100%',
                      minHeight: 82,
                      height: 'auto',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                      whiteSpace: 'normal',
                      padding: '12px 14px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minWidth: 0 }}>
                      <Icon fontSize='small' />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                        <Text
                          strong
                          style={{
                            color: isSelected ? '#fff' : undefined,
                            whiteSpace: 'normal',
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {iconOption.label}
                        </Text>
                        <Text
                          type='secondary'
                          style={{
                            color: isSelected ? 'rgba(255,255,255,0.86)' : undefined,
                            fontSize: 12,
                            whiteSpace: 'normal',
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {iconOption.key}
                        </Text>
                      </div>
                    </div>
                  </Button>
                );
              })}
            </div>
          </div>
        </Space>
      </Modal>

      <ReorderModal
        open={isReorderModalOpen}
        title="Reordenar tiers"
        items={serviceTiers.map((tier) => ({ id: tier.id!, name: tier.name }))}
        onClose={() => setIsReorderModalOpen(false)}
        onSave={async (items) => {
          await reorderServiceTiers(items);
          await fetchData();
        }}
      />
    </ContainerLayout>
  );
};

export default ServiceTierList;
